import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { PlusIcon } from '@heroicons/react/24/outline';
import { parsearTareaRapida, resumenTareaRapida } from '../../utils/tareasRapidas';
import { isoLocal } from '../../utils/tareasFechas';
import { hoyLocal } from '../../utils/fechaLocal';
import type { Assignee, TaskItem } from './types';

interface Props {
  assignees: Assignee[];
  canAssign: boolean;
  defaultAssigneeId: string;
  onCreated: (task: TaskItem) => void;
}

/**
 * Una línea y Enter: "Pedir cubiertas a Vetaro @rodrigo mañana 15:00 !alta".
 * Abajo se muestra cómo se leyó antes de guardar.
 */
const TaskQuickAdd: React.FC<Props> = ({ assignees, canAssign, defaultAssigneeId, onCreated }) => {
  const [texto, setTexto] = useState('');
  const [guardando, setGuardando] = useState(false);
  const hoy = hoyLocal();
  const parsed = useMemo(() => parsearTareaRapida(texto, canAssign ? assignees : [], hoy), [texto, assignees, canAssign, hoy]);
  const resumen = texto.trim() ? resumenTareaRapida(parsed, hoy) : '';

  const crear = async () => {
    if (!parsed.title) return;
    setGuardando(true);
    try {
      const res = await axios.post('/api/tasks', {
        title: parsed.title,
        priority: parsed.priority,
        dueAt: parsed.dueYmd ? isoLocal(parsed.dueYmd, parsed.dueTime || (parsed.dueYmd === hoy ? '18:00' : '09:00')) : null,
        assignedTo: parsed.assigneeId || defaultAssigneeId
      });
      onCreated(res.data?.task);
      setTexto('');
      toast.success(parsed.assigneeName ? `Tarea creada para ${parsed.assigneeName.split(' ')[0]}` : 'Tarea creada');
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'No se pudo crear la tarea');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <form
      onSubmit={(e) => { e.preventDefault(); crear(); }}
      className="rounded-lg border border-border bg-card px-3 py-2 shadow-sm"
    >
      <div className="flex items-center gap-2.5">
        <span className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-md border border-dashed border-muted-foreground text-muted-foreground">
          <PlusIcon className="h-3.5 w-3.5" strokeWidth={2.5} />
        </span>
        <input
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          disabled={guardando}
          placeholder={canAssign ? 'Agregar tarea y Enter… ej: Pedir cubiertas a Vetaro @rodrigo mañana 15:00 !alta' : 'Agregar tarea y Enter… ej: Calibrar la alineadora viernes 09:00'}
          aria-label="Agregar tarea"
          className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground/70"
        />
        <div className="hidden shrink-0 gap-1.5 lg:flex">
          {(canAssign ? ['@persona', 'hoy · mañana · lunes', '15:00', '!alta · !urgente'] : ['hoy · mañana · lunes', '15:00', '!alta']).map((h) => (
            <span key={h} className="rounded border border-border px-1.5 py-px text-[11px] text-muted-foreground">{h}</span>
          ))}
        </div>
      </div>
      {texto.trim() && (
        <div className="mt-1 flex flex-wrap items-center gap-x-2 pl-[34px] text-xs text-muted-foreground">
          <span className="truncate">{parsed.title || '…'}</span>
          {resumen && <span className="rounded-full bg-accent px-2 py-px text-foreground">{resumen}</span>}
          <span className="ml-auto hidden sm:inline">Enter para crear</span>
        </div>
      )}
    </form>
  );
};

export default TaskQuickAdd;
